import { useMemo, useCallback } from 'react';
import { Task, FullProject } from '../types';
import {
  extractProjectPersons,
  isPersonRecord,
  isPurePersonContact,
  normalizePersonName,
} from '../components/communication/personModel';

interface UseSidebarPeopleControllerParams {
  allProjects: FullProject[];
  allTasksGlobal: Task[];
  tasks: Task[];
  currentProjectId: string;
  viewMode: string;
  activeWorkspace: 'professional' | 'private';
  setFilterAssignee: React.Dispatch<React.SetStateAction<string>>;
  setEditingTask: (task: Task | null) => void;
  handleAddTask: (
    type?: Task['type'],
    parentId?: string | number,
    projectId?: string,
    name?: string,
    initialData?: Partial<Task>,
    options?: { bypassDragGuard?: boolean }
  ) => Task | undefined;
}

export function useSidebarPeopleController({
  allProjects,
  allTasksGlobal,
  tasks,
  currentProjectId,
  viewMode,
  activeWorkspace,
  setFilterAssignee,
  setEditingTask,
  handleAddTask,
}: UseSidebarPeopleControllerParams) {
  const peopleWithActiveVorgang = useMemo(() => {
    const names = new Set<string>();
    allTasksGlobal.forEach(t => {
      if (t.archived || t.completed || t.status === 'completed') return;
      const isVorgang = t.contactId !== undefined && t.contactId !== null && t.contactId !== '';
      const isCommVorgang =
        t.category === 'Kommunikation' &&
        (t.status === 'open' || t.status === 'pending') &&
        !isPurePersonContact(t);
      if ((isVorgang || isCommVorgang) && t.assignee) {
        t.assignee.split(/[,;/]+/).forEach(p => {
          const clean = p.trim();
          if (clean) names.add(clean.toLowerCase());
        });
      }
    });
    return names;
  }, [allTasksGlobal]);

  const { sidebarPeople, sidebarPeopleData } = useMemo(() => {
    return extractProjectPersons({
      allProjects,
      allTasksGlobal,
      activeTasks: tasks,
      projectId: currentProjectId,
      viewMode,
      activeWorkspace,
      todayISO: new Date().toISOString().split('T')[0],
    });
  }, [allProjects, tasks, activeWorkspace, viewMode, currentProjectId, allTasksGlobal]);

  const handleToggleSidebarPersonFilter = useCallback(
    (name: string) => {
      setFilterAssignee(prev => (prev === name || name === 'all' ? 'all' : name));
    },
    [setFilterAssignee]
  );

  const handleOpenSidebarContactCard = useCallback(
    (name: string) => {
      const norm = name.trim().toLowerCase();
      const cleanNorm = normalizePersonName(name);
      const isPerson = (t: Task) =>
        t.type === 'contact' ||
        t.status === 'pool' ||
        isPurePersonContact(t) ||
        isPersonRecord(t) ||
        (typeof t.id === 'string' && t.id.startsWith('pool_contact_'));

      const contactTask = allTasksGlobal.find(
        t =>
          isPerson(t) &&
          (t.name?.trim().toLowerCase() === norm ||
            t.assignee?.trim().toLowerCase() === norm ||
            (cleanNorm && normalizePersonName(t.name || t.assignee) === cleanNorm))
      );

      if (contactTask) {
        setEditingTask(contactTask);
      } else {
        const isVirtual =
          !currentProjectId ||
          currentProjectId === 'global' ||
          currentProjectId === 'globalKanban' ||
          currentProjectId === 'communication';
        const targetProjectId = isVirtual ? 'comm_internal' : currentProjectId;
        const activeRealProjects = allProjects.filter(
          p =>
            p &&
            p.id &&
            !p.archived &&
            !p.id.startsWith('comm_internal') &&
            p.id !== '__comm__' &&
            !p.id.startsWith('inbox_inforum') &&
            (!p.workspace || p.workspace === activeWorkspace)
        );
        const defaultProjectIds = isVirtual
          ? activeRealProjects.length > 0
            ? [activeRealProjects[0].id]
            : []
          : [currentProjectId];
        const newContact = handleAddTask('contact', undefined, targetProjectId, name, {
          name,
          assignee: name,
          type: 'contact',
          status: 'pool',
          category: 'Kommunikation',
          workspace: activeWorkspace,
          projectIds: defaultProjectIds,
          isInlineNew: true,
        });
        if (newContact) {
          setEditingTask(newContact);
        }
      }
    },
    [allProjects, allTasksGlobal, currentProjectId, handleAddTask, activeWorkspace, setEditingTask]
  );

  const handleAddPersonToProject = useCallback(
    (personData?: {
      name?: string;
      role?: string;
      organization?: string;
      email?: string;
      phone?: string;
    }) => {
      const isVirtual =
        !currentProjectId ||
        currentProjectId === 'global' ||
        currentProjectId === 'globalKanban' ||
        currentProjectId === 'communication';
      const targetProjectId = isVirtual ? 'comm_internal' : currentProjectId;
      const activeRealProjects = allProjects.filter(
        p =>
          p &&
          p.id &&
          !p.archived &&
          !p.id.startsWith('comm_internal') &&
          p.id !== '__comm__' &&
          !p.id.startsWith('inbox_inforum') &&
          (!p.workspace || p.workspace === activeWorkspace)
      );
      const defaultProjectIds = isVirtual
        ? activeRealProjects.length > 0
          ? [activeRealProjects[0].id]
          : []
        : [currentProjectId];
      const name = personData?.name || '';
      const newContact = handleAddTask('contact', undefined, targetProjectId, name, {
        name,
        assignee: name,
        role: personData?.role,
        organization: personData?.organization,
        contactEmail: personData?.email,
        contactPhone: personData?.phone,
        type: 'contact',
        status: 'pool',
        category: 'Kommunikation',
        workspace: activeWorkspace,
        projectIds: defaultProjectIds,
        isInlineNew: true,
      });
      if (newContact) {
        setEditingTask(newContact);
      }
    },
    [allProjects, currentProjectId, handleAddTask, activeWorkspace, setEditingTask]
  );

  return {
    peopleWithActiveVorgang,
    sidebarPeople,
    sidebarPeopleData,
    handleToggleSidebarPersonFilter,
    handleOpenSidebarContactCard,
    handleAddPersonToProject,
  };
}
